import { useLocation, useNavigate } from 'react-router-dom';
import { format, eachDayOfInterval } from 'date-fns';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './index.css';
import { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import { collection, addDoc, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { FaArrowLeft } from "react-icons/fa";

function BookingConfirm() { 
  const { state } = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const { villa, dates, adults, children, nights, total } = state || {};
  const totalGuests = adults + children;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contactDetails, setContactDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gstNumber: '',
    referredBy: '',
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isValidUser, setIsValidUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error('Failed to load Razorpay script'));
        document.body.appendChild(script);
      });
    };

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setIsValidUser(false);
        setLoading(false);
        toast.success('Please login to proceed with booking!', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsValidUser(!!userData.role);
          setContactDetails(prev => ({
            ...prev,
            email: currentUser.email || prev.email,
            firstName: userData.firstName || prev.firstName,
            lastName: userData.lastName || prev.lastName
          }));
        }
      } catch (error) {
        setIsValidUser(false);
      }
      setLoading(false);
    });

    const initialize = async () => {
      try {
        await loadRazorpayScript();
      } catch (error) {
        toast.error('Failed to initialize payment gateway. Please refresh the page.', {
          position: 'top-right',
          autoClose: 3000,
        });
        setLoading(false);
      }
    };

    initialize();
    return () => {
      unsubscribe();
      const script = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (script) document.body.removeChild(script);
    };
  }, [auth]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleButtonClick = () => {
    if (!auth.currentUser) {
      toast.error('Please login to make payment', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    if (!isValidUser) {
      toast.error('Unauthorized: Please login with a valid account to book', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    handlePayment();
  };

  const handlePayment = async () => {
    if (!auth.currentUser) {
      toast.error('Please login to make payment', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
      return;
    }

    if (!isValidUser) {
      toast.error('Unauthorized: Please login with a valid account to book', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
      return;
    }

    if (!contactDetails.firstName || !contactDetails.lastName || !contactDetails.email || !phoneNumber) {
      toast.error('Please fill all required contact details', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
      return;
    }

    if (!window.Razorpay) {
      toast.error('Payment gateway not loaded. Please refresh the page.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
      return;
    }

    const amountInPaise = total * 100;
    if (amountInPaise > 100000000) {
      toast.error('Amount exceeds maximum allowed limit. Please contact support for large transactions.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
      return;
    }

    setIsButtonDisabled(true);

    try {
      const response = await fetch('https://luxe-backend.netlify.app/payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          amount: amountInPaise,
          userId: auth.currentUser.uid,
        }),
      });

      const order = await response.json();

      if (!response.ok) {
        throw new Error(order.message || 'Failed to create payment order');
      }

      function generateBookingId(length = 8) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
      }


      // eslint-disable-next-line no-unused-vars
      const options = {
        key: 'rzp_test_2U3wItuISJsrjG',
        amount: order.amount,
        currency: 'INR',
        name: 'Luxe Unbounded',
        description: `Booking for ${villa.title}`,
        order_id: order.id,
        handler: async function (response) {
          try {
            const blockedDates = eachDayOfInterval({
              start: dates.startDate,
              end: new Date(dates.endDate.getTime() - 24 * 60 * 60 * 1000),
            }).map((date) => format(date, 'yyyy-MM-dd'));

            const bookingId = generateBookingId();

            const bookingRef = await addDoc(collection(db, 'bookings'), {
              bookingId,
              userId: auth.currentUser.uid,
              villaId: villa.id,
              villaTitle: villa.title,
              checkIn: dates.startDate,
              checkOut: dates.endDate,
              nights,
              totalGuests,
              totalPrice: total,
              contactDetails: {
                firstName: contactDetails.firstName,
                lastName: contactDetails.lastName,
                email: contactDetails.email,
                phoneNumber,
                gstNumber: contactDetails.gstNumber,
                referredBy: contactDetails.referredBy,
              },
              status: 'confirmed',
              createdAt: new Date(),
              paymentDate: new Date(),
              paymentId: response.razorpay_payment_id,
            });

            const villaRef = doc(db, 'villas', villa.id);
            await updateDoc(villaRef, {
              blockedDates: arrayUnion(...blockedDates),
            });

            try {
      const response = await fetch('https://luxe-backend.netlify.app/send-booking-emails', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  customerEmail: contactDetails.email,
                  customerName: `${contactDetails.firstName} ${contactDetails.lastName}`,
                  bookingId,
                  villa: villa.title,
                  checkIn: format(dates.startDate, 'dd MMM yyyy'),
                  checkOut: format(dates.endDate, 'dd MMM yyyy'),
                  total,
                  phoneNumber,
                  gstNumber: contactDetails.gstNumber,
                  referredBy: contactDetails.referredBy,
                }),
              });
            } catch (mailError) {
              toast.error('Error sending booking emails. Please contact support.', {
                position: 'top-right',
                autoClose: 3000,
              });
            }

            toast.success('Payment successful! Booking confirmed.', {
              position: 'top-right',
              autoClose: 3000,
            });
            navigate('/my-bookings', {
              state: { bookingId: bookingRef.id, villa, dates, total },
            });
          } catch (error) {
            toast.error('Error processing booking. Please contact support.', {
              position: 'top-right',
              autoClose: 3000,
            });
            setIsButtonDisabled(false);
          }
        },
        prefill: {
          name: `${contactDetails.firstName} ${contactDetails.lastName}`,
          email: contactDetails.email,
          contact: phoneNumber,
        },
        theme: {
          color: '#3399cc',
        },
        modal: {
          ondismiss: () => {
            setIsButtonDisabled(false);
            toast.info('Payment cancelled. You can try again.', {
              position: 'top-right',
              autoClose: 3000,
            });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setIsButtonDisabled(false);
        const errorMessage = response.error.description || 'Payment failed. Please try again.';
        if (errorMessage.includes('exceeds maximum amount')) {
          toast.error('Amount exceeds maximum allowed limit. Please contact support for large transactions.', {
            position: 'top-right',
            autoClose: 3000,
          });
        } else {
          toast.error(errorMessage, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      });
      rzp.open();
    } catch (error) {
      toast.error('Error initiating payment. Please try again or contact support.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsButtonDisabled(false);
    }
  };

  if (loading) return <div className="spinner-container">
        <div className="spinner"></div>
    </div>;
  if (!villa || !dates || !dates.startDate || !dates.endDate) {
    return <div className="error"><ToastContainer />No booking details available</div>;
  }

  const checkIn = format(dates.startDate, 'dd MMM yyyy');
  const checkOut = format(dates.endDate, 'dd MMM yyyy');

  return (
    <div className="villa-detail-container">
      <ToastContainer />
      <button className="back-btn" onClick={() => navigate(-1)}><FaArrowLeft /> Back to Villa</button>
      <div className="detail-header">
        <div className="detail-left">
          <img src={villa.imageUrls[1]} alt={villa.title} className="detail-image" />
          <div className="detail-info">
            <h2 className="detail-subtitle">REVIEW YOUR BOOKING</h2>
            <p className="location">{villa.location || 'Ooty, Tamil Nadu, India'}</p>
            <p className="villa-name">{villa.title || 'Hiawatha By The Brook'}</p>
            <p className="villa-details">
              {totalGuests} Guests • {villa.bedrooms || 6} Bedrooms • {villa.bathrooms || 6.5} Bathrooms
            </p>
            <p className="villa-price">
              ₹{(parseFloat(villa.basePricePerNight) || 0).toFixed(2)} / Night
            </p>
          </div>
        </div>
        <div className="detail-right">
          <img src="/logo.jpeg" alt="Logo" className="logo-image" />
        </div>
      </div>
      <div className="stay-details">
        <h3 className="stay-title">Stay Details</h3>
        <div className="stay-row">
          <div>
            <p>Check-In & Check-Out</p>
            <p>{`${checkIn} - ${checkOut}`}</p>
          </div>
          <div>
            <p>Total Nights</p>
            <p>{nights} Night{nights !== 1 ? 's' : ''}</p>
          </div>
          <div>
            <p>Guests</p>
            <p>{totalGuests} Guest{totalGuests !== 1 ? 's' : ''}</p>
          </div>
          <div>
            <p>Booking Type</p>
            <p>Villa</p>
          </div>
        </div>
      </div>
      <div className="summary-section">
        <h3 className="summary-title">SUMMARY</h3>
        <div className="summary-details">
          <div className="summary-row">
            <span>Villa x {nights} Nights</span>
            <span>₹{(nights * (parseFloat(villa.basePricePerNight) || 0)).toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>VAT / GST</span>
            <span>₹{((nights * (parseFloat(villa.basePricePerNight) || 0)) * (parseFloat(villa.vatGst) / 100 || 0.18)).toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Refundable Security Deposit</span>
            <span>₹{(parseFloat(villa.securityDeposit) || 0).toFixed(2)}</span>
          </div>
          <div className="summary-row total">
            <span>Total Price</span>
            <span>₹{total.toFixed(2)}</span>
          </div>
        </div>
      </div>
      <div className="contact-sectionb">
        <div className="contact-headerb">
          <h3 className="contact-titleb">CONTACT DETAILS</h3>
        </div>
        <div className="contact-inputs">
          <div className="contact-rowb">
            <input
              type="text"
              name="firstName"
              placeholder="First Name *"
              className="contact-input"
              value={contactDetails.firstName}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name *"
              className="contact-input"
              value={contactDetails.lastName}
              onChange={handleInputChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Email ID *"
              className="contact-input"
              value={contactDetails.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="contact-rowb">
            <div className="phone-input-wrapper">
              <PhoneInput
                international
                defaultCountry="IN"
                value={phoneNumber}
                onChange={setPhoneNumber}
                placeholder="Phone Number *"
                className="contact-input phone-input"
                required
              />
            </div>
            <input
              type="text"
              name="gstNumber"
              placeholder="GST Number"
              className="contact-input"
              value={contactDetails.gstNumber}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="referredBy"
              placeholder="Referred by"
              className="contact-input"
              value={contactDetails.referredBy}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <p className="terms-text">
          By clicking on the Payment button, you will be agreeing to the Terms and conditions and Privacy Policy.
        </p>
        <button
          className="payment-btn"
          onClick={handleButtonClick}
          disabled={isButtonDisabled}
        >
          MAKE PAYMENT
        </button>
      </div>
    </div>
  );
}
export default BookingConfirm;